import type { SuggestionPlan, SuggestionPlanCheck } from '@/api/client'

/**
 * Client-side source gathering for AI suggestions (REFACTOR_FILE_ACCESS.md).
 *
 * Walks a user-picked local folder via the File System Access API and collects,
 * per audit check, the excerpts matching the server-provided plan (globs, skip
 * dirs, extension allow-list, byte/file budgets). Only these excerpts ever
 * leave the machine — the user reviews the exact list before submission.
 *
 * Mirrors the BFF's FileRelevanceRanker walk semantics: skip-dirs are pruned
 * without descending, extension allow-list applies before glob matching, and
 * the file that crosses the byte budget is truncated with the same marker.
 */

// ── Minimal FS Access API typings (lib.dom doesn't ship the picker/iterator) ──

interface DirHandle {
  readonly kind: 'directory'
  readonly name: string
  values(): AsyncIterableIterator<DirHandle | FileHandle>
}

interface FileHandle {
  readonly kind: 'file'
  readonly name: string
  getFile(): Promise<File>
}

declare global {
  interface Window {
    showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite' }) => Promise<DirHandle>
  }
}

/** Chromium (and Electron) only — Firefox/Safari fallback is a deferred follow-up. */
export function isFolderPickSupported(): boolean {
  return typeof window.showDirectoryPicker === 'function'
}

/** Prompts the native folder picker. Returns null if the user cancels. */
export async function pickSourceFolder(): Promise<DirHandle | null> {
  try {
    return (await window.showDirectoryPicker!({ mode: 'read' })) ?? null
  } catch (err) {
    // AbortError = user cancelled the picker; anything else is a real failure.
    if (err instanceof DOMException && err.name === 'AbortError') return null
    throw err
  }
}

export interface GatheredFile {
  path: string
  content: string
  truncated: boolean
  bytes: number
}

export interface GatheredCheck {
  auditCheckId: string
  title: string
  files: GatheredFile[]
}

const MAX_WALK_DEPTH = 16
/** Safety valve against pathological trees the skip-list doesn't catch. */
const MAX_CANDIDATE_FILES = 20_000

/**
 * Walks the folder once, then matches the collected candidates against each
 * check's globs, reading file contents lazily and within the plan's budgets.
 */
export async function gatherExcerpts(
  root: DirHandle,
  plan: SuggestionPlan,
): Promise<GatheredCheck[]> {
  const skipDirs = new Set(plan.skipDirs)
  const allowedExtensions = new Set(plan.allowedExtensions)

  const candidates: { path: string; handle: FileHandle }[] = []
  await collectCandidates(root, '', 0, skipDirs, allowedExtensions, candidates)

  const out: GatheredCheck[] = []
  for (const check of plan.checks) {
    out.push(await gatherForCheck(check, candidates, plan))
  }
  return out
}

async function collectCandidates(
  dir: DirHandle,
  prefix: string,
  depth: number,
  skipDirs: Set<string>,
  allowedExtensions: Set<string>,
  out: { path: string; handle: FileHandle }[],
): Promise<void> {
  if (depth > MAX_WALK_DEPTH || out.length >= MAX_CANDIDATE_FILES) return
  for await (const entry of dir.values()) {
    if (out.length >= MAX_CANDIDATE_FILES) return
    if (entry.kind === 'directory') {
      if (skipDirs.has(entry.name)) continue
      await collectCandidates(entry, `${prefix}${entry.name}/`, depth + 1, skipDirs, allowedExtensions, out)
    } else {
      const dot = entry.name.lastIndexOf('.')
      if (dot < 0) continue
      if (!allowedExtensions.has(entry.name.slice(dot + 1).toLowerCase())) continue
      out.push({ path: `${prefix}${entry.name}`, handle: entry })
    }
  }
}

async function gatherForCheck(
  check: SuggestionPlanCheck,
  candidates: { path: string; handle: FileHandle }[],
  plan: SuggestionPlan,
): Promise<GatheredCheck> {
  const matchers = check.globs.map(globToRegExp)
  const files: GatheredFile[] = []
  let budget = plan.maxBytesPerCheck

  for (const candidate of candidates) {
    if (files.length >= plan.maxFilesPerCheck || budget <= 0) break
    if (!matchers.some(re => re.test(candidate.path))) continue

    const file = await candidate.handle.getFile()
    // Read at most the remaining budget + 1 byte so we can tell "fits exactly"
    // from "needs truncation" without pulling a huge file into memory.
    const blob = file.slice(0, budget + 1)
    let content = await blob.text()
    let bytes = byteLength(content)
    let truncated = false
    if (bytes > budget) {
      content = truncateToBytes(content, budget) + '\n…(truncated)…'
      bytes = budget
      truncated = true
    }
    files.push({ path: candidate.path, content, truncated, bytes })
    budget -= bytes
  }

  return { auditCheckId: check.id, title: check.title, files }
}

// ── Glob matching — same subset the BFF's PathMatcher hints use ──────────────
// `**` crosses path segments, `*` matches within a segment. Patterns starting
// with `**/` also match root-level files (mirrors FileRelevanceRanker's
// bare-filename variant).

export function globToRegExp(glob: string): RegExp {
  const body = glob
    .replace(/[.+^${}()|[\]\\?]/g, '\\$&')
    // One pass so `**` wins over `*`; a two-pass version needs a sentinel
    // character, which can collide with the input.
    .replace(/\*\*|\*/g, (star) => (star === '**' ? '.*' : '[^/]*'))
  // For `**/x` patterns, body is `.*/x...` -- the alternative without the
  // leading `.*/` (3 chars) matches root-level files.
  const source = glob.startsWith('**/')
    ? `^(?:${body}|${body.slice(3)})$`
    : `^${body}$`
  return new RegExp(source)
}

const encoder = new TextEncoder()

function byteLength(s: string): number {
  return encoder.encode(s).length
}

/** Cuts the string so its UTF-8 encoding fits maxBytes, without splitting a code point. */
function truncateToBytes(s: string, maxBytes: number): string {
  if (byteLength(s) <= maxBytes) return s
  let lo = 0
  let hi = s.length
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2)
    if (byteLength(s.slice(0, mid)) <= maxBytes) lo = mid
    else hi = mid - 1
  }
  return s.slice(0, lo)
}
